import axios from 'axios';
import {FAILURE, REQUEST, SUCCESS} from 'app/shared/reducers/action-type.util';

import {defaultValue, IPatientCase} from 'app/shared/model/patient-case.model';
import {defaultValue as dashboardCountsDefaultValue} from 'app/shared/model/dispatcher-dashboard-count.model';
import {IDispatcherDashboardCase} from 'app/shared/model/dispatcher-dashboard-case.model';

export const ACTION_TYPES = {
  FETCH_PATIENT_CASES_BY_STATUS_AND_DISPATCH_CENTER: 'patientCase/FETCH_PATIENT_CASES_BY_STATUS_AND_DISPATCH_CENTER',
  FETCH_DISPATCHER_DASHBOARD_COUNTS: 'patientCase/FETCH_DISPATCHER_DASHBOARD_COUNTS',
  SAVE_PATIENT_CASE_FOLLOWUP: 'patientCase/SAVE_PATIENT_CASE_FOLLOWUP',
  RESET_FOLLOWUP: 'patientCase/RESET_FOLLOWUP',
  RESET: 'patientCase/RESET',
};

const initialState = {
  loading: false,
  dashboardCountsLoading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<IPatientCase>,
  dashboardCases: [] as ReadonlyArray<IDispatcherDashboardCase>,
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  dashboardCounts: dashboardCountsDefaultValue,
  followupSaving: false,
  followupSaveSuccess: false,
};

export type PatientCaseState = Readonly<typeof initialState>;

// Reducer
export default (state: PatientCaseState = initialState, action): PatientCaseState => {
  switch (action.type) {
    case REQUEST(ACTION_TYPES.FETCH_PATIENT_CASES_BY_STATUS_AND_DISPATCH_CENTER):
      return {
        ...state,
        errorMessage: null,
        updateSuccess: false,
        loading: true,
      };
    case REQUEST(ACTION_TYPES.FETCH_DISPATCHER_DASHBOARD_COUNTS):
      return {
        ...state,
        errorMessage: null,
        updateSuccess: false,
        dashboardCountsLoading: true,
      };
    case REQUEST(ACTION_TYPES.SAVE_PATIENT_CASE_FOLLOWUP):
      return {
        ...state,
        errorMessage: null,
        followupSaving: true,
        followupSaveSuccess: false,
      };
    case FAILURE(ACTION_TYPES.FETCH_PATIENT_CASES_BY_STATUS_AND_DISPATCH_CENTER):
      return {
        ...state,
        loading: false,
        updating: false,
        updateSuccess: false,
        errorMessage: action.payload,
      };
    case FAILURE(ACTION_TYPES.FETCH_DISPATCHER_DASHBOARD_COUNTS):
      return {
        ...state,
        dashboardCountsLoading: false,
        updating: false,
        updateSuccess: false,
        errorMessage: action.payload,
      };
    case FAILURE(ACTION_TYPES.SAVE_PATIENT_CASE_FOLLOWUP):
      return {
        ...state,
        followupSaving: false,
        followupSaveSuccess: false,
        errorMessage: action.payload,
      };
    case SUCCESS(ACTION_TYPES.FETCH_PATIENT_CASES_BY_STATUS_AND_DISPATCH_CENTER):
      return {
        ...state,
        loading: false,
        dashboardCases: action.payload.data || [],
        totalItems: action.payload.data?.length || 0,
      };
    case SUCCESS(ACTION_TYPES.FETCH_DISPATCHER_DASHBOARD_COUNTS):
      return {
        ...state,
        dashboardCountsLoading: false,
        dashboardCounts: action.payload.data,
      };
    case SUCCESS(ACTION_TYPES.SAVE_PATIENT_CASE_FOLLOWUP):
      return {
        ...state,
        followupSaving: false,
        followupSaveSuccess: true,
        entity: action.payload.data,
      };
    case ACTION_TYPES.RESET_FOLLOWUP:
      return {
        ...state,
        followupSaving: false,
        followupSaveSuccess: false,
        errorMessage: null
      };
    case ACTION_TYPES.RESET:
      return {
        ...initialState,
      };
    default:
      return state;
  }
};

const apiUrl = 'api/patient-cases';

// Actions
export const getPatientCasesByStatusAndDispatchCenter = (
  caseStatuses: string[],
  dispatchCenterId?: string,
  countryId?: string,
  pendingFeedback?: boolean,
  page?: number,
  size?: number,
  sort?: string
) => {
  let requestUrl = `${apiUrl}/by-status-and-dispatch-center?`;

  // Add parameters based on what's provided
  const params = [];

  if (pendingFeedback === true) {
    params.push(`pendingFeedback=true`);
  } else if (caseStatuses && caseStatuses.length > 0) {
    const caseStatusesParam = caseStatuses.map(status => `caseStatuses=${status}`).join('&');
    params.push(caseStatusesParam);
  }

  if (dispatchCenterId) {
    params.push(`dispatchCenterId=${dispatchCenterId}`);
  }

  if (countryId) {
    params.push(`countryId=${countryId}`);
  }

  if (page !== undefined && size !== undefined && sort) {
    params.push(`page=${page}&size=${size}&sort=${sort}`);
  }

  requestUrl += params.join('&');

  return {
    type: ACTION_TYPES.FETCH_PATIENT_CASES_BY_STATUS_AND_DISPATCH_CENTER,
    payload: axios.get<IDispatcherDashboardCase[]>(requestUrl),
  };
};

export const getDispatcherDashboardCounts = (dispatchCenterId?: string, countryId?: string) => {
  let requestUrl = `${apiUrl}/dispatcher-dashboard-counts`;
  const params = [];

  if (dispatchCenterId) {
    params.push(`dispatchCenterId=${dispatchCenterId}`);
  }

  if (countryId) {
    params.push(`countryId=${countryId}`);
  }

  if (params.length > 0) {
    requestUrl += `?${params.join('&')}`;
  }

  return {
    type: ACTION_TYPES.FETCH_DISPATCHER_DASHBOARD_COUNTS,
    payload: axios.get(requestUrl),
  };
};

export const savePatientCaseFollowup = (followupData: any) => {
  return {
    type: ACTION_TYPES.SAVE_PATIENT_CASE_FOLLOWUP,
    payload: axios.post('api/patient-case-followup', followupData),
  };
};

export const resetPatientCaseFollowup = () => dispatch => {
  return dispatch({
    type: ACTION_TYPES.RESET_FOLLOWUP,
  });
}

export const reset = () => dispatch => {
  return dispatch({
    type: ACTION_TYPES.RESET,
  });
};
